import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Limits saved from the Portfolio Monitoring drawers this session (MESA-5129), keyed by
 * `${grain}:${rowId}`. The APR list endpoints read invoice-service materialized views that
 * only refresh on the scheduled RefreshViews task, so after a save every refetch (including a
 * new filter's) returns the old limit until the next refresh. `applyLimitEdits` merges these
 * edits over the server rows until the views catch up.
 *
 * Deliberately NOT in the redux-persist whitelist: an edit only needs to cover the gap until the
 * next view refresh, and a persisted entry could hide a later change made somewhere else.
 */
export type LimitEditGrain = 'vendor' | 'externalClient';

export type LimitEdit = {
  readonly limit: number;
  /** Client clock, epoch ms. */
  readonly savedAt: number;
};

export type PortfolioLimitEditsState = {
  edits: Record<string, LimitEdit>;
};

export const initialState: PortfolioLimitEditsState = {
  edits: {},
};

export const limitEditKey = (grain: LimitEditGrain, rowId: string): string => `${grain}:${rowId}`;

const portfolioLimitEditsSlice = createSlice({
  name: 'portfolioLimitEdits',
  initialState,
  reducers: {
    recordLimitEdit(state, action: PayloadAction<{ grain: LimitEditGrain; rowId: string; limit: number; savedAt: number }>) {
      const { grain, rowId, limit, savedAt } = action.payload;
      state.edits[limitEditKey(grain, rowId)] = { limit, savedAt };
    },
    pruneLimitEdits(state, action: PayloadAction<readonly string[]>) {
      for (const key of action.payload) delete state.edits[key];
    },
  },
});

export const { recordLimitEdit, pruneLimitEdits } = portfolioLimitEditsSlice.actions;

export default portfolioLimitEditsSlice.reducer;
